/**
 * material_models — Atlas's side of the material-models round (shader gaps program round 4, 2026-09-30): the three
 * flags with their kill switches, the per-family parameters, and the `__materialModels()` lever.
 *
 *   • `eonDiffuse` — the engine's energy-preserving Oren–Nayar diffuse (render/materials/EONDiffuse.ts) on the rough
 *     rock families (geology rock of the rocky highlands, the regolith plains, the desert) and the lit lime plaster of
 *     the personal-world homes (`lib/plaster_material.ts`). Kill: `atlas:eon = 'off'`.
 *   • `laplacianBlend` — Wronski's Laplacian blend on ONE ground transition (`LAPLACIAN_PAIR`, lib/wetness.ts) — the
 *     low frequencies cross over a wide ramp, the detail over the height blend's narrow one. Kill: `atlas:laplacian`.
 *   • `flakeGlints` — the footprint-filtered statistical flakes (render/materials/FlakeGlints.ts) on the slate of the
 *     crystal forest, replacing its old emissive glint (unlit, view-independent, unfiltered). Kill: `atlas:flakes`.
 * Every one is built into a material when it is made (the rocks' cache, the ground's next build) — a reload applies a
 * change (`VR_DECISIONS`: keep(PARSE)).
 */
import type { Biome } from '@shared/terrain.js';
import type { GroundLayerKey } from './ground_layers.js';
import { activePreset } from './quality_tiers.js';
import { NORMAL_CONTRACT, normalContractSummary } from './normal_contract.js';

type Flag = 'eonDiffuse' | 'laplacianBlend' | 'flakeGlints';
const KILL: Record<Flag, string> = { eonDiffuse: 'atlas:eon', laplacianBlend: 'atlas:laplacian', flakeGlints: 'atlas:flakes' };

function killed(f: Flag): boolean {
  try { return localStorage.getItem(KILL[f]) === 'off'; } catch { return false; }
}
function flagOn(f: Flag): boolean {
  if (killed(f)) return false;
  return activePreset()[f] === true;
}
export const eonDiffuseOn = (): boolean => flagOn('eonDiffuse');
export const laplacianBlendOn = (): boolean => flagOn('laplacianBlend');
export const flakeGlintsOn = (): boolean => flagOn('flakeGlints');

/**
 * The EON DIFFUSE roughness per rock palette (0 = Lambert). Weathered granite, sandstone and desert crusts are the
 * classic Oren–Nayar surfaces (Oren & Nayar 1994 measured plaster, sand and clay); the slate, basalt and tuff are
 * smoother cleavage faces and keep Lambert. Rough, never 1 — a rock face is not a pile of loose grains.
 */
export const EON_ROCK_ROUGHNESS: Readonly<Record<Biome, number>> = {
  rocky_highlands: 0.75, regolith_plains: 0.85, dune_desert: 0.9, crystal_forest: 0, moss_wetland: 0, fungal_basin: 0,
};

/**
 * The EON diffuse roughness of LIT plaster — the personal-world homes' lime plaster (`lib/plaster_material.ts`); settlement
 * interiors are unlit by law (interiors.md: INTERIOR MATERIALS ARE UNLIT), so a diffuse model reaches no other plaster.
 * Troweled plaster is one of Oren & Nayar's measured rough surfaces, but smoother than a weathered rock face — rougher
 * than a painted wall (0), below the rock families (0.75–0.9).
 */
export const EON_PLASTER_ROUGHNESS = 0.6;

/**
 * The slate's mica (the crystal forest's blue-grey rock): flakes per geometry unit² (the rock geometry is ~metre-sized),
 * their GGX spread, Deliot–Belcour's glint ratio, coverage and reflectance. Tuned on the material bed.
 */
export const SLATE_FLAKES = { density: 9000, flakeRoughness: 0.45, ratio: 0.06, coverage: 0.35, f0: 0.35, color: [0.82, 0.9, 1.0] as [number, number, number] };

/** The ground transition that takes the Laplacian blend (picked on the bed — see impl-material-models.md). */
export const LAPLACIAN_PAIR: readonly [GroundLayerKey, GroundLayerKey] = ['gravel', 'rock'];
let pairOverride: readonly [GroundLayerKey, GroundLayerKey] | null = null;
/** The pair the ground builds with (the shipped `LAPLACIAN_PAIR` unless the material bed compares another). */
export function laplacianPair(): readonly [GroundLayerKey, GroundLayerKey] { return pairOverride ?? LAPLACIAN_PAIR; }
/** DEV-ONLY (the material bed): build the next ground with another pair; null restores the shipped one. */
export function setLaplacianPairForBed(pair: readonly [GroundLayerKey, GroundLayerKey] | null): void { pairOverride = pair; }
/** Laplacian levels above the pixel's own mip (Wronski: 3–5; each costs two coarser taps per layer). */
export const LAPLACIAN_LEVELS = 3;
/**
 * 'full' = every Laplacian level blends on its own ramp (Wronski's Eq. 3); 'gaussian' = only the Gaussian level (the
 * mean colour) glides on the wide ramp and the detail keeps the height blend (Brown & Lowe's two-band form — two taps).
 */
export type LaplacianMode = 'full' | 'gaussian';
export const LAPLACIAN_MODE: LaplacianMode = 'gaussian';
let modeOverride: LaplacianMode | null = null;
export function laplacianMode(): LaplacianMode { return modeOverride ?? LAPLACIAN_MODE; }
/** DEV-ONLY (the material bed): build the next ground in another mode; null restores the shipped one. */
export function setLaplacianModeForBed(mode: LaplacianMode | null): void { modeOverride = mode; }
/** The coarse levels' mask half-width in height-blend units, doubled a level (Eq. 13's s·2^k). */
export const LAPLACIAN_MASK_S = 0.3;

/** Build-time facts (the lever reads them; the bed and the harness pin them). */
export const materialModelStats = { eonRocks: 0, eonPlaster: 0, flakeRocks: 0, laplacianGrounds: 0, routedGround: 0, routedRock: 0 };

declare global { interface Window { __materialModels?: () => unknown } }
if (typeof window !== 'undefined') {
  window.__materialModels = () => ({
    flags: { eonDiffuse: eonDiffuseOn(), laplacianBlend: laplacianBlendOn(), flakeGlints: flakeGlintsOn() },
    killed: { eonDiffuse: killed('eonDiffuse'), laplacianBlend: killed('laplacianBlend'), flakeGlints: killed('flakeGlints') },
    kill: KILL,
    stats: { ...materialModelStats },
    eonRoughness: EON_ROCK_ROUGHNESS, eonPlasterRoughness: EON_PLASTER_ROUGHNESS, slateFlakes: SLATE_FLAKES,
    laplacian: { pair: LAPLACIAN_PAIR, levels: LAPLACIAN_LEVELS, maskS: LAPLACIAN_MASK_S },
    normalContract: normalContractSummary(),
    contributors: () => NORMAL_CONTRACT.map((c) => `${c.id} · ${c.op} · ${c.form} · ${c.space}`),
    note: 'the flags apply when a material is built (the rock cache, the ground\'s next build) — reload after a change',
  });
}
